// Écran « Profil » du shell mobile Life — DA V3 validée : compte,
// abonnement + crédits, notifications, langue, déconnexion.
// Réutilise la boîte de dialogue UserOptions existante (réglages complets)
// et la même séquence de déconnexion que la NavBar desktop.
// Réf. : DEV/mockup_mobile_navigation_v3.html
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import Button from '@mui/material/Button'
import UserOptions from '../UserOptions'
import { setSiteUser } from 'reduxLib/slices/userSlice'
import { clearClientSession } from 'utils/session'
import { makePostRequest } from 'utils/api'
import { unregisterPushDevice } from 'utils/push'
import {
  TAB_CATALOG,
  getCatalogEntry,
  loadTabSlots,
  saveTabSlots,
} from './tabCatalog'

const MobileProfile = () => {
  const { t, i18n } = useTranslation(['layout', 'common'])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const siteUser = useSelector((store) => store.user)
  const user = siteUser?.user || {}

  const [uoption, setUoption] = React.useState(false)
  const [logoutOpen, setLogoutOpen] = React.useState(false)
  // Personnalisation des 2 onglets du milieu de la tab bar (stockée sur
  // l'appareil ; saveTabSlots notifie la barre via l'événement dédié).
  const [tabsOpen, setTabsOpen] = React.useState(false)
  const [slots, setSlots] = React.useState(loadTabSlots)

  const pickSlot = (slotIndex, key) => {
    const next = [...slots]
    // Si la destination est déjà sur l'autre slot, on échange pour éviter
    // deux onglets identiques.
    const other = slotIndex === 0 ? 1 : 0
    if (next[other] === key) next[other] = next[slotIndex]
    next[slotIndex] = key
    setSlots(next)
    saveTabSlots(next)
  }

  const setUser = (u) => dispatch(setSiteUser(u))

  const handleLogout = async () => {
    // Même séquence que la NavBar desktop, puis rechargement complet :
    // en WebView, repartir d'un état propre évite tout résidu (userSync,
    // stores, websockets).
    try {
      // AVANT la purge du token de session (endpoint authentifié) :
      // l'appareil ne doit plus recevoir les push de ce compte.
      await unregisterPushDevice()
      await makePostRequest('/api/user/logout/', {}, {}, true)
    } catch (_) {
      // Même en cas d'échec réseau on déconnecte localement.
    }
    clearClientSession(dispatch)
    window.location.href = '/login'
  }

  const credits =
    user?.credits ?? user?.credit_balance ?? user?.remaining_credits

  return (
    <div className="lm-page">
      <div className="lm-stitre">{t('profile.account', 'Compte')}</div>

      <div className="lm-prow" onClick={() => setUoption(true)}>
        <div className="lm-pi">👤</div>
        <div className="lm-fl">
          {t('profile.myAccount', 'Mon compte')}
          {user?.email && <small>{user.email}</small>}
        </div>
        <div className="lm-ch">›</div>
      </div>

      <div className="lm-prow" onClick={() => navigate('/choose-plan')}>
        <div className="lm-pi">⭐</div>
        <div className="lm-fl">
          {t('profile.subscription', 'Abonnement & crédits')}
          {credits !== undefined && (
            <small>
              {credits} {t('profile.creditsLeft', 'crédits restants')}
            </small>
          )}
        </div>
        <div className="lm-ch">›</div>
      </div>

      <div className="lm-stitre">{t('profile.preferences', 'Préférences')}</div>

      <div className="lm-prow" onClick={() => navigate('/inbox')}>
        <div className="lm-pi">🔔</div>
        <div className="lm-fl">{t('profile.notifications', 'Notifications')}</div>
        <div className="lm-ch">›</div>
      </div>

      <div className="lm-prow" onClick={() => setUoption(true)}>
        <div className="lm-pi">🌐</div>
        <div className="lm-fl">
          {t('profile.language', 'Langue')}
          <small>{(i18n.language || 'fr').split('-')[0].toUpperCase()}</small>
        </div>
        <div className="lm-ch">›</div>
      </div>

      <div className="lm-prow" onClick={() => setTabsOpen(true)}>
        <div className="lm-pi">🧩</div>
        <div className="lm-fl">
          {t('profile.customizeTabs', 'Personnaliser la barre')}
          <small>
            {slots
              .map((k) => {
                const e = getCatalogEntry(k)
                return e ? t(e.label[0], e.label[1]) : k
              })
              .join(' · ')}
          </small>
        </div>
        <div className="lm-ch">›</div>
      </div>

      <div className="lm-stitre">{t('profile.session', 'Session')}</div>

      <div className="lm-prow" onClick={() => setLogoutOpen(true)}>
        <div className="lm-pi">🚪</div>
        <div className="lm-fl" style={{ color: '#ef4444' }}>
          {t('profile.logout', 'Déconnexion')}
        </div>
      </div>

      {uoption && (
        <UserOptions user={user} setUser={setUser} setUoption={setUoption} />
      )}

      {/* Choix des 2 onglets personnalisables — un groupe par emplacement,
          présenté dans la DA sombre du shell (classes lm-tabpick*). */}
      <Dialog
        open={tabsOpen}
        onClose={() => setTabsOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: 'lm-tabpick' }}
      >
        <DialogContent>
          <h3>{t('profile.customizeTabs', 'Personnaliser la barre')}</h3>
          <p className="lm-tabpick-aide">
            {t(
              'profile.customizeTabsHint',
              "Choisissez les 2 raccourcis du milieu. Aujourd'hui et Profil restent toujours accessibles."
            )}
          </p>
          {[0, 1].map((slotIndex) => (
            <div key={slotIndex}>
              <div className="lm-tabpick-slot">
                {t('profile.tabSlot', 'Emplacement')} {slotIndex + 1}
              </div>
              <div className="lm-tabpick-grid">
                {TAB_CATALOG.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    className={`lm-tabpick-opt${
                      slots[slotIndex] === entry.key ? ' lm-actif' : ''
                    }`}
                    onClick={() => pickSlot(slotIndex, entry.key)}
                  >
                    {entry.icon}
                    <span>{t(entry.label[0], entry.label[1])}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTabsOpen(false)}>
            {t('buttons.close', 'Fermer', { ns: 'common' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <DialogContent>
          <DialogContentText>
            {t('dialogs.logoutConfirm', 'Voulez-vous vraiment vous déconnecter ?', { ns: 'common' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout}>{t('buttons.yes', 'Oui', { ns: 'common' })}</Button>
          <Button onClick={() => setLogoutOpen(false)}>{t('buttons.no', 'Non', { ns: 'common' })}</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default MobileProfile
